import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, dots, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * Foldfire (the Origami set's gun, ADR-0160 amended): a launcher folded out of paper. A cream stock fan-folded in
 * pleats, a white body of flat facets (a lit top, a crease along its side, a shaded foot) with a red seal stamped on it,
 * a red-and-white folding fan standing on its back that breathes open and shut, a barrel of pleated paper rings to a
 * folded cone at the muzzle. On a shot (`fire`) the fan snaps wide open, the cone flashes white, then it settles.
 */

/** A fan of pleats standing on its pivot at (cx, cy): `open` its half-angle (radians), `r` its reach. */
function fan(c: PixelCanvas, cx: number, cy: number, r: number, open: number, flash: boolean): void {
  for (let y = Math.floor(cy - r); y <= cy; y++)
    for (let x = Math.floor(cx - r); x <= cx + r; x++) {
      const [dx, dy] = [x + 0.5 - cx, y + 0.5 - cy];
      const d = Math.hypot(dx, dy);
      if (d > r || d < 1.2) continue;
      // From straight up: negative to the left.
      const a = Math.atan2(dx, -dy);
      if (Math.abs(a) > open) continue;
      const pleat = Math.floor(((a + open) / (2 * open)) * 8);
      const rim = d > r - 1.1;
      const red = pleat % 2 === 0;
      c.set(x, y, flash ? (rim ? '5' : 'w') : rim ? (red ? 'R' : '4') : red ? (a < 0 ? 'i' : 'r') : a < 0 ? 'w' : '5');
    }
  // Its ribs meeting at the pivot.
  c.set(Math.floor(cx), Math.floor(cy), 'N').set(Math.floor(cx) - 1, Math.floor(cy), 'm');
}

/** The pleated rings of the barrel: a stripe a column, white, grey, shaded in turn. */
const rings =
  (frame: number) =>
  (top: number, bottom: number, x: number): PaletteKey => {
    const k = (x + frame) % 3;
    if (top === 0) return k === 2 ? '5' : 'w';
    if (bottom === 0) return '3';
    return k === 0 ? 'w' : k === 1 ? '5' : '4';
  };

const OPEN = [0.95, 1.05, 1.12, 1.05] as const;

const foldfire = (frame: number, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The stock: cream paper fan-folded, a pleat every two columns.
    box(c, 5, 16, 22, 25, [2, 1], (x, y) => (y === 16 ? 'e' : y >= 24 ? 'm' : x % 4 < 2 ? 'e' : 't'), 'l');
    // The fan on its back.
    fan(c, 34, 12, 10, heat >= 2 ? 1.45 : heat === 1 ? 1.3 : (OPEN[frame % 4] as number), heat === 3);
    // The body: white facets, a lit top, a crease along its side, its foot shaded.
    box(c, 21, 12, 46, 27, [2, 1], byRow(12, 27, ['3', '4', '5', 'w'], 2, 2), 'r');
    for (let x = 23; x <= 44; x++) c.set(x, 19, '4');
    for (let y = 14; y <= 25; y++) c.set(23 + Math.floor((y - 14) * 0.5), y, y < 19 ? '5' : '4');
    // The red seal stamped on its side, a white mark in it.
    box(c, 36, 20, 42, 25, [0], (x, y) => (x === 36 || y === 20 ? 'r' : x === 42 || y === 25 ? 'R' : 'r'), 'r');
    dots(c, 'w', [
      [38, 22],
      [39, 22],
      [40, 22],
      [39, 23],
      [38, 24],
      [40, 24],
    ]);
    // The trigger.
    box(c, 30, 28, 33, 30, [0, 1], (_x, y) => (y === 28 ? 't' : 'm'), 'r');
    // The barrel of pleated rings, a folded cone at the muzzle.
    taper(c, 47, 57, 19.5, 3.4, 2.8, rings(frame));
    taper(c, 58, 62, 19.5, 3, 1.2, heat ? () => 'w' : round(['4', '5', 'w', 'w']));
    dots(c, heat ? 'w' : '4', [
      [58, 18],
      [58, 21],
    ]);
  });

export default sprite({
  name: 'pollinator_origami',
  ...geometry,
  notes: `Foldfire (ADR-0160 amended, the Origami set's gun): a launcher folded out of paper. A cream stock fan-folded in
pleats, a white body of flat facets (a lit top, a crease along its side, a shaded foot) with a red seal stamped on it, a
red-and-white folding fan standing on its back breathing open and shut over 4 frames, a barrel of pleated paper rings
(their pleats running down it) to a folded cone at the muzzle. Fire: the fan snaps wide open and flashes white, the cone
flashes, settles over 3 frames. Its flair: a soft white glow breathing at the fan, flecks of paper puffed from the
muzzle. No team keys. Rubric (4×): S4 R5 P4 L4 C4 A4 F4. Weakest: white on a white gardener's armour, its slate sel-out
and the red fan carry it.`,
  anims: {
    idle: { fps: 5, frames: [foldfire(0), foldfire(1), foldfire(2), foldfire(3)] },
    fire: { fps: 20, loop: false, frames: [foldfire(0, 3), foldfire(1, 2), foldfire(2, 1), foldfire(3)] },
  },
});
